import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IdentityApi } from '@backstage/core-plugin-api';

/**
 * Handler component that checks for bypass auth tokens in URL params.
 * If found, it establishes the session and cleans up the URL.
 */
export function BypassAuthHandler({ 
  onSignInSuccess 
}: { 
  onSignInSuccess?: (identityApi: IdentityApi) => void 
}) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!onSignInSuccess) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const urlToken = params.get('__bypass_token');
    
    // Check for token in URL params (new login) or sessionStorage (existing session)
    const token = urlToken || sessionStorage.getItem('__bypass_token');
    
    if (!token) {
      return;
    }

    let email: string;
    let sub: string;

    if (urlToken) {
      // New login from URL params
      email = params.get('__bypass_email') || '';
      sub = params.get('__bypass_sub') || `user:default/${email}`;

      // Store token in sessionStorage for persistence
      sessionStorage.setItem('__bypass_token', token);
      sessionStorage.setItem('__bypass_email', email);
      sessionStorage.setItem('__bypass_sub', sub);
    } else {
      // Existing session from sessionStorage
      email = sessionStorage.getItem('__bypass_email') || '';
      sub = sessionStorage.getItem('__bypass_sub') || `user:default/${email}`;
    }

    // Create IdentityApi implementation that reads from sessionStorage
    const identityApi: IdentityApi = {
      getProfileInfo: async () => {
        const storedEmail = sessionStorage.getItem('__bypass_email') || email;
        return {
          email: storedEmail,
          displayName: storedEmail.split('@')[0],
        };
      },
      getBackstageIdentity: async () => {
        const storedSub = sessionStorage.getItem('__bypass_sub') || sub;
        return {
          type: 'user',
          userEntityRef: storedSub,
          ownershipEntityRefs: [storedSub],
        };
      },
      getCredentials: async () => {
        const storedToken = sessionStorage.getItem('__bypass_token') || token;
        return {
          token: storedToken,
        };
      },
      signOut: async () => {
        sessionStorage.removeItem('__bypass_token');
        sessionStorage.removeItem('__bypass_email');
        sessionStorage.removeItem('__bypass_sub');
      },
    };

    // Clean up URL by removing bypass params only if they were in the URL
    if (urlToken) {
      params.delete('__bypass_token');
      params.delete('__bypass_email');
      params.delete('__bypass_sub');
      
      const cleanPath = params.toString() 
        ? `${location.pathname}?${params.toString()}` 
        : location.pathname;
      
      // Replace URL to remove token from history
      navigate(cleanPath, { replace: true });
    }

    // Establish the session (on every page load if token exists)
    onSignInSuccess(identityApi);
  }, [location, navigate, onSignInSuccess]);

  return null;
}
